import { Uuid } from '@/api/types';
import {
  AirportLegLocationReference,
  CommercialLegType,
  CustomAddressLegLocationReference,
  CustomCrewMember,
  GroundLocationReference,
  LogisticsLegResourceType,
  RouteLegTypeEnum,
  SegmentTypeEnum,
  TransportProviderTypes,
} from '@/api/apis/route';

export enum GroundVehicleType {
  Sedan = 'Sedan',
  SUV = 'SUV',
}

export type LegChainPosition = {
  groupId: string;
  previousLegId: string | null;
  nextLegId: string | null;
};

export type BaseRouteLeg = {
  id: Uuid;
  legType: RouteLegTypeEnum;
  segmentType: SegmentTypeEnum;
  chainPosition: LegChainPosition;
  comments?: string;
};

export type BaseRouteLegData<T = AirportLegLocationReference, R = LogisticsLegResourceType> = BaseRouteLeg & {
  departureLocation: T;
  arrivalLocation: T;
  departureTime: string;
  arrivalTime: string;
  departureLocalTimezone: string;
  arrivalLocalTimezone: string;
  resources: R[];
  hasScs: boolean;
};

export type TmdxDriveTripReference = {
  tripId: Uuid;
  tripNumber: string;
};

// Avia

export type TmdxJetAviaLeg<T = AirportLegLocationReference, R = LogisticsLegResourceType> = BaseRouteLegData<T, R> & {
  legType: RouteLegTypeEnum.TmdxJetAviaLeg;
};

export type SupplementalLiftAviaLeg<T = AirportLegLocationReference, R = LogisticsLegResourceType> = BaseRouteLegData<
  T,
  R
> & {
  legType: RouteLegTypeEnum.SupplementalLiftAviaLeg;
  tailNumber?: string;
  priceInCents: number;
  firstCrewMember: CustomCrewMember;
  secondCrewMember: CustomCrewMember;
  provider: {
    providerId: Uuid;
    providerType: TransportProviderTypes.SupplementalLiftProvider;
  };
};

export type TxCenterProviderAviaLeg<T = AirportLegLocationReference, R = LogisticsLegResourceType> = BaseRouteLegData<
  T,
  R
> & {
  legType: RouteLegTypeEnum.TxCenterProviderAviaLeg;
  tailNumber?: string;
  firstCrewMember: CustomCrewMember;
  secondCrewMember: CustomCrewMember;
  provider: {
    providerId: Uuid;
    providerType: TransportProviderTypes.TxCenter;
  };
};

export type CommercialFlightAviaLeg<T = AirportLegLocationReference, R = LogisticsLegResourceType> = BaseRouteLegData<
  T,
  R
> & {
  legType: RouteLegTypeEnum.CommercialFlightAviaLeg;
  flightNumber: string;
  bookingNumber: string;
  priceInCents: number;
  sabreGroupId?: string;
  seatType?: string;
  numberOfSeats?: number;
  flightOperator?: string;
  commercialLegType: CommercialLegType;
};

// Ground

export type GroundLegBaseData = {
  distanceInMeters: number;
  isFeasibleForDrive: boolean;
};

export type NonTmdxGroundLeg<T = GroundLocationReference, R = LogisticsLegResourceType> = BaseRouteLegData<T, R> &
  GroundLegBaseData & {
    driver?: string;
    email?: string;
    phoneNumber?: string;
    plateNumber?: string;
    vehicleType?: GroundVehicleType;
    reservationNumber?: string;
    pickup?: string;
    dropoff?: string;
    wait?: string;
    stay?: string;
  };

export type TmdxAffiliateGroundLeg<T = GroundLocationReference, R = LogisticsLegResourceType> = NonTmdxGroundLeg<
  T,
  R
> & {
  legType: RouteLegTypeEnum.TmdxAffiliateGroundLeg;
};

export type BaseTmdxDriveAffiliateGroundLeg<
  T = GroundLocationReference,
  R = LogisticsLegResourceType,
> = BaseRouteLegData<T, R> & {
  distanceInMeters: number;
  isFeasibleForDrive: boolean;
  tripReference?: TmdxDriveTripReference;
};

export type TmdxDriveAffiliateGroundLeg<
  T = GroundLocationReference,
  R = LogisticsLegResourceType,
> = BaseTmdxDriveAffiliateGroundLeg<T, R> & {
  legType: RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg;
};

export type TxCenterProviderGroundLeg<T = GroundLocationReference, R = LogisticsLegResourceType> = NonTmdxGroundLeg<
  T,
  R
> & {
  legType: RouteLegTypeEnum.TxCenterProviderGroundLeg;
  provider: {
    providerId: Uuid;
    providerType: TransportProviderTypes.TxCenter;
  };
};

export type CurriServiceGroundLeg<T = GroundLocationReference, R = LogisticsLegResourceType> = BaseRouteLegData<
  T,
  R
> & {
  legType: RouteLegTypeEnum.CurriServiceGroundLeg;
  trackingNumber?: string;
  distanceInMeters: number;
};

export type TmdxDriveTedsGroundLeg<
  T = GroundLocationReference,
  R = LogisticsLegResourceType,
> = BaseTmdxDriveAffiliateGroundLeg<T, R> & {
  legType: RouteLegTypeEnum.TmdxDriveTedsGroundLeg;
};

export type SelfServicedGroundLeg<T = GroundLocationReference, R = LogisticsLegResourceType> = BaseRouteLegData<T, R> &
  GroundLegBaseData & {
    legType: RouteLegTypeEnum.SelfServicedGroundLeg;
  };

// Accommodations

export type AccommodationLeg<T = CustomAddressLegLocationReference, R = LogisticsLegResourceType> = Omit<
  BaseRouteLeg & {
    legType: RouteLegTypeEnum.AccommodationLeg;
    checkInAt: string;
    checkOutAt: string;
    hotelName: string;
    rooms: number;
    bookingNumber?: string;
    location: T | null;
    resources: R[];
    hasScs: boolean;
  },
  'chainPosition'
>;

export type GroundLegTypes =
  | TmdxAffiliateGroundLeg
  | TxCenterProviderGroundLeg
  | CurriServiceGroundLeg
  | TmdxDriveAffiliateGroundLeg
  | TmdxDriveTedsGroundLeg
  | SelfServicedGroundLeg;
export type AviaLegTypes = TmdxJetAviaLeg | SupplementalLiftAviaLeg | TxCenterProviderAviaLeg | CommercialFlightAviaLeg;

export type RouteLegType = AviaLegTypes | GroundLegTypes | AccommodationLeg;
